import Edit from "@mui/icons-material/Edit";
import OpenInNew from "@mui/icons-material/OpenInNew";
import { Box, IconButton, Link, Stack, SxProps, Theme, Tooltip, Typography } from "@mui/material";
import { SubmissionLinkedRecords } from "components/Submission/SubmissionLinkedRecords";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { SubmissionSource } from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useTenant } from "hooks/useTenant";
import { useWasteStepper } from "hooks/useWasteStepper";
import { useEffect, useMemo, useState } from "react";
import { V2DisplayOnlyField as DisplayOnlyField } from "routes/Customer/Facility/ProfileV2/V2DisplayOnlyField";
import { useFacilityRcraProfile } from "routes/Customer/Facility/useFacility";
import { formatDateLikeForDisplay } from "util/dates";
import { buildWasteFacilityUrl } from "util/wasteUrls";

import {
  EditSubmissionDetailsDialog,
  type DueDateShiftConfig,
} from "./EditSubmissionDetailsDialog";
import { formatReasonForSubmittal } from "./reasonForSubmittalLabels";
import { StepSubHeader } from "./StepSubHeader";

type StaffAssignee = NonNullable<ReturnType<typeof useWasteStepper>["submission"]>["staffAssignee"];

type AssigneeValue = { id: string; name: string; email: string } | null;

function toAssigneeValue(assignee: StaffAssignee): AssigneeValue {
  if (!assignee) return null;
  const name =
    [assignee.person?.first, assignee.person?.last].filter(Boolean).join(" ") || assignee.email;
  return { id: assignee.id, name, email: assignee.email };
}

/**
 * "Name (email)" for display, matching the reviewer/verifier pickers. Falls
 * back to just the email when we have no name (so it never reads
 * "email (email)").
 */
function formatAssignee(assignee: AssigneeValue): string {
  if (!assignee) return "";
  if (!assignee.email || assignee.name === assignee.email) return assignee.name;
  return `${assignee.name} (${assignee.email})`;
}

function DocumentFormSummaryFields({
  facilities,
  tenantId,
  recipient,
  submissionMethod,
  rowSx,
}: {
  facilities: Array<{
    id: string;
    name: string;
    customerFacilityId: string | null;
    epaId: string | null;
    address: string;
  }>;
  tenantId: string;
  recipient: string;
  submissionMethod: string;
  rowSx: SxProps<Theme>;
}) {
  return (
    <>
      <DisplayOnlyField
        label={facilities.length === 1 ? "Facility" : "Facilities"}
        text={facilities.map((facility) => facility.name).join(", ")}
        // One site per line with its EPA ID, so every step shows the same thing.
        renderValue={() => (
          <Stack spacing={0.5} alignItems="flex-start">
            {facilities.map((facility) => (
              <Stack
                key={facility.id}
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
              >
                <Link
                  href={buildWasteFacilityUrl(tenantId, facility.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body1"
                  underline="hover"
                  sx={{
                    wordBreak: "break-word",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  {facility.name}
                  <OpenInNew sx={{ fontSize: 16 }} />
                </Link>
                <Typography variant="body2" color="text.secondary">
                  {[facility.customerFacilityId, facility.epaId, facility.address]
                    .filter(Boolean)
                    .join(" · ")}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      />
      <Box sx={rowSx}>
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <DisplayOnlyField label="Recipient" text={prettyPrintEnumValue(recipient)} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <DisplayOnlyField
            label="Submission Method"
            text={prettyPrintEnumValue(submissionMethod)}
          />
        </Box>
      </Box>
    </>
  );
}

/**
 * Submission-level details (title, due date, Encamp assignee, and — for
 * document forms — facility, recipient, and submission method), rendered
 * inline at the top of the stepper Overview rather than as its own step —
 * it's metadata about the submission, a level above the per-section data
 * entry.
 *
 * Always rendered read-only; editors (facility write access, and not frozen)
 * get a pencil that opens `EditSubmissionDetailsDialog` to change the values,
 * mirroring the facility profile's section-edit pattern. The 8700-12 title is
 * derived from the facility + form (not user-chosen), so it is not editable;
 * document forms have a user-chosen title, editable in the dialog. Facility,
 * recipient, and submission method are also document-form-only.
 *
 * `dueDateShift` lets a due-date change offer to shift reviewer due dates.
 * `hideEditAction` drops the pencil for surfaces that own their own edit
 * affordance, so the two don't read as competing ways to edit the same thing.
 */
export function DetailsCard({
  dueDateShift,
  hideEditAction,
}: { dueDateShift?: DueDateShiftConfig; hideEditAction?: boolean } = {}) {
  const { isStaff } = useCurrentUser();
  const { tenantId = "" } = useTenant();
  const { submission, canEdit, detailsEditRequest } = useWasteStepper();
  const [editOpen, setEditOpen] = useState(false);

  // A validation alert elsewhere on the step asked for these fields.
  useEffect(() => {
    if (detailsEditRequest > 0) setEditOpen(true);
  }, [detailsEditRequest]);

  const title = submission?.title ?? "";
  const dueDate = submission?.dueDate ?? null;
  const staffAssignee = submission?.staffAssignee ?? null;
  const submissionId = submission?.id ?? "";
  const assignee = toAssigneeValue(staffAssignee);

  // §1 Reason for Submittal and §18 Public Comments are 8700-12 form fields,
  // shown only for an actual interactive 8700-12 (usesInteractiveStepper, not
  // the (type, method) combo — matches WasteSubmissionStepper's isDocumentForm).
  const isRcra870012 = submission?.usesInteractiveStepper ?? false;

  // A "new site" 8700-12 is one whose facility has no active EPA ID yet:
  // submitting registers the site and requests a new ID (subject to EPA/state
  // approval), so §1 drops the discontinuation reason. Only fetched for 8700-12;
  // `epaIdStatuses` can include inactive/withdrawn IDs, so only an active one
  // counts. While the profile loads (statuses undefined) we treat the site as
  // existing, so the new-site notice/gating never flashes on for a known site.
  const facilitySubmissions = submission?.facilitySubmissions ?? [];
  const facilitySubmission = facilitySubmissions[0];
  const facilityId = facilitySubmission?.facility?.id ?? "";
  // Same fields the legacy form's facility grid shows. A hasNoId alternate id is
  // a recorded absence, not an id.
  const facilities = facilitySubmissions.map(({ facility }) => ({
    id: facility.id,
    name: facility.name,
    customerFacilityId: facility.customerFacilityId ?? null,
    epaId:
      facility.facilityAlternateIds?.find((alt) => alt.type === "EPA" && !alt.hasNoId)?.value ??
      null,
    address: [
      facility.streetAddress1,
      [facility.city, facility.state].filter(Boolean).join(", "),
      facility.zip,
    ]
      .filter(Boolean)
      .join(", "),
  }));
  const recipient = submission?.recipient ?? "";
  const submissionMethod = submission?.submissionMethod ?? "";
  const { data: rcraData } = useFacilityRcraProfile(isRcra870012 ? facilityId : "");
  const isNewSite = useMemo(() => {
    const statuses = rcraData?.facility?.epaIdStatuses;
    if (!statuses) return false;
    return !statuses.some((status) => !status.isInactive && !!status.epaId);
  }, [rcraData?.facility?.epaIdStatuses]);

  // Title and the §1/§18 form fields each get their own row; the wide Encamp
  // Assignee and the compact Due date share a row to use the horizontal space.
  const formSx = {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    maxWidth: 760,
  } as const;

  // Encamp Assignee (flexes to fill) + Due date (fixed compact width) on one row,
  // wrapping to two rows on narrow viewports.
  const rowSx = {
    display: "flex",
    gap: 2,
    flexWrap: "wrap",
    alignItems: "flex-start",
  } as const;

  return (
    <Box
      sx={{
        // Tinted so metadata reads as context, not another field group.
        // grey50.main is the mode-aware token; palette.grey[50] is near-white
        // in both modes and turns this into a white block on the dark theme.
        backgroundColor: "grey50.main",
        borderRadius: 1,
        p: 2,
        "& .edit-icon": { opacity: 0, transition: "opacity 200ms" },
        "&:hover .edit-icon": { opacity: 1 },
      }}
    >
      <Stack spacing={2}>
        <StepSubHeader
          title="Submission Details"
          action={
            canEdit &&
            !hideEditAction && (
              <Tooltip title="Edit Submission Details">
                <IconButton
                  size="small"
                  className="edit-icon"
                  sx={{ color: "action.active" }}
                  onClick={() => setEditOpen(true)}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
            )
          }
        />
        <Box sx={formSx}>
          <DisplayOnlyField label="Title" text={title} />
          {!isRcra870012 && (
            <DocumentFormSummaryFields
              facilities={facilities}
              tenantId={tenantId}
              recipient={recipient}
              submissionMethod={submissionMethod}
              rowSx={rowSx}
            />
          )}
          <Box sx={rowSx}>
            {/* Encamp Assignee is an internal staff assignment — staff-only. */}
            {isStaff && (
              <Box sx={{ flex: 1, minWidth: 280 }}>
                <DisplayOnlyField label="Encamp Assignee" text={formatAssignee(assignee)} />
              </Box>
            )}
            <Box sx={{ width: 220 }}>
              <DisplayOnlyField
                label="Due date"
                text={dueDate ? formatDateLikeForDisplay(dueDate) : ""}
              />
            </Box>
          </Box>
          {isRcra870012 ? (
            <>
              <DisplayOnlyField
                label="Reason for Submittal (§1)"
                text={formatReasonForSubmittal(submission?.reasonForSubmittal)}
              />
              <DisplayOnlyField label="Public Comments (§18)" text={submission?.notes ?? ""} />
            </>
          ) : (
            <DisplayOnlyField label="Notes" text={submission?.notes ?? ""} />
          )}
          <SubmissionLinkedRecords
            tenantId={tenantId}
            wasteStreams={(submission?.wasteStreamSubmissions ?? []).map(
              (link) => link.wasteStream
            )}
            registrations={(submission?.stateWasteStreamRegistrationSubmissions ?? []).map(
              (link) => link.registration
            )}
          />
        </Box>
      </Stack>

      {editOpen && (
        <EditSubmissionDetailsDialog
          submissionId={submissionId}
          isStaff={isStaff}
          isRcra870012={isRcra870012}
          isNewSite={isNewSite}
          // Document forms (everything that isn't the derived-title 8700-12) get
          // an editable, required title.
          allowTitleEdit={!isRcra870012}
          initialTitle={title}
          initialDueDate={dueDate}
          initialAssignee={assignee}
          initialNotes={submission?.notes ?? ""}
          initialReasonForSubmittal={submission?.reasonForSubmittal ?? ""}
          initialRecipient={recipient}
          initialSubmissionMethod={submissionMethod || ""}
          initialFacilities={facilities}
          canEditFacilities={submission?.source !== SubmissionSource.System}
          dueDateShift={dueDateShift}
          onClose={() => setEditOpen(false)}
        />
      )}
    </Box>
  );
}
